import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Snackbar,
  TextField
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
  AccountTree as AccountTreeIcon,
  Visibility as VisibilityIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { User, UserHierarchyNode, AuthorizationLevelInfo } from '../../model';
import { useAppState } from '../../components/AppProvider/AppProvider';
import HierarchyTreeView from '../../components/UserHierarchy/HierarchyTreeView';
import UserDetailPanel from '../../components/UserHierarchy/UserDetailPanel';
import ReportingChainView from '../../components/UserHierarchy/ReportingChainView';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(1.5),
    backgroundColor: '#f5f5f5',
    minHeight: '100vh',
  },
  header: {
    marginBottom: theme.spacing(1.5),
  },
  headerTitle: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: '1.3rem',
  },
  widget: {
    marginBottom: theme.spacing(1.5),
    borderRadius: theme.spacing(0.5),
    boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
  },
  widgetHeader: {
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.grey[50],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  widgetTitle: {
    fontWeight: 500,
    fontSize: '0.85rem',
    display: 'flex',
    alignItems: 'center',
  },
  widgetContent: {
    padding: theme.spacing(1.5),
  },
  compactButtons: {
    display: 'flex',
    gap: theme.spacing(0.8),
    flexWrap: 'wrap',
  },
  compactButton: {
    fontSize: '0.75rem',
    padding: theme.spacing(0.4, 1.2),
    textTransform: 'none',
    minHeight: '28px',
  },
  treeWidget: {
    height: 'calc(100vh - 200px)',
    minHeight: '400px',
    overflow: 'auto',
  },
  sideWidget: {
    maxHeight: '500px',
    overflow: 'auto',
  },
  levelChip: {
    margin: theme.spacing(0.2),
    fontSize: '0.7rem',
    height: '20px',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '300px',
  },
  unassignedDialog: {
    minWidth: '500px',
    padding: 0, // Remove default padding to control layout better
  },
  dialogSearchSection: {
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  dialogListSection: {
    padding: 0,
    maxHeight: '400px',
    overflow: 'auto',
  },
  userItem: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
}));

const UserHierarchyPage: React.FC = () => {
  const classes = useStyles();
  const [ctx] = useAppState() ?? [];
  
  // State management
  const [hierarchyTree, setHierarchyTree] = useState<UserHierarchyNode[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [unassignedUsers, setUnassignedUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | undefined>();
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set());
  const [authorizationLevels, setAuthorizationLevels] = useState<AuthorizationLevelInfo[]>([]);
  const [reportingChain, setReportingChain] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUnassignedDialog, setShowUnassignedDialog] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [collapseAll, setCollapseAll] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Load initial data
  useEffect(() => {
    loadHierarchyData();
  }, []);

  const buildHierarchyTreeFromUsers = (userList: User[]): UserHierarchyNode[] => {
    const userMap = new Map<number, UserHierarchyNode>();
    const rootNodes: UserHierarchyNode[] = [];

    // Create nodes for all users
    userList.forEach(user => {
      userMap.set(user.id, {
        user,
        children: [],
        level: 0,
      });
    });

    // Build the tree structure
    userList.forEach(user => {
      const node = userMap.get(user.id)!;
      
      if (user.reportTo && user.reportTo.id) {
        const parentNode = userMap.get(user.reportTo.id);
        if (parentNode) {
          parentNode.children.push(node);
          node.level = parentNode.level + 1;
        } else {
          rootNodes.push(node);
        }
      } else {
        rootNodes.push(node);
      }
    });

    // Calculate levels properly
    const calculateLevels = (nodes: UserHierarchyNode[], level: number) => {
      nodes.forEach(node => {
        node.level = level;
        calculateLevels(node.children, level + 1);
      });
    };

    calculateLevels(rootNodes, 0);
    return rootNodes;
  };

  const loadHierarchyData = async () => {
    try {
      setLoading(true);
      setError(null);

      const headers = {
        'authorization': ctx?.apiKey ?? '',
        'Content-Type': 'application/json'
      };

      // Load authorization levels
      try {
        const authResponse = await fetch(`${ctx?.baseUrl}/user-hierarchy/authorization-levels`, { headers });
        if (authResponse.ok) {
          const response = await authResponse.json();
          setAuthorizationLevels(response.levels || response);
        } else {
          throw new Error('Authorization levels endpoint not available');
        }
      } catch (authError) {
        // Fallback to default authorization levels
        console.warn('Using default authorization levels:', authError);
        setAuthorizationLevels([
          { value: 1, name: "Director", description: "Executive level" },
          { value: 2, name: "Manager", description: "Management level" },
          { value: 3, name: "Supervisor", description: "Supervisory level" },
          { value: 4, name: "Team Lead", description: "Team leadership" },
          { value: 5, name: "Staff", description: "Regular staff" },
          { value: 6, name: "Operator", description: "Operational level" },
        ]);
      }

      // Load all users first
      const usersResponse = await fetch(`${ctx?.baseUrl}/users/view`, { headers });
      if (usersResponse.ok) {
        const users = await usersResponse.json();
        setAllUsers(users);
        
        // Update selected user with fresh data if one is currently selected
        if (selectedUser) {
          const updatedSelectedUser = users.find((u: User) => u.id === selectedUser.id);
          if (updatedSelectedUser) {
            console.log('[UserHierarchyPage] Updating selected user with fresh data');
            setSelectedUser(updatedSelectedUser);
            
            // Also refresh the reporting chain for the selected user
            try {
              const chainResponse = await fetch(`${ctx?.baseUrl}/user-hierarchy/reporting-chain/${updatedSelectedUser.id}`, { headers });
              if (chainResponse.ok) {
                const chain = await chainResponse.json();
                setReportingChain(chain);
                console.log('[UserHierarchyPage] Updated reporting chain for selected user');
              }
            } catch (chainError) {
              console.error('Error updating reporting chain:', chainError);
            }
          }
        }
        
        // Find unassigned users (users with no reportTo and no subordinates)
        const unassigned = users.filter((user: User) => {
          const hasManager = user.reportTo && user.reportTo.id != null;
          const hasSubordinates = users.some((u: User) => u.reportTo?.id === user.id);
          return !hasManager && !hasSubordinates;
        });
        setUnassignedUsers(unassigned);
      }

      // Load hierarchy tree
      try {
        const treeResponse = await fetch(`${ctx?.baseUrl}/user-hierarchy/tree`, { headers });
        if (treeResponse.ok) {
          const tree = await treeResponse.json();
          setHierarchyTree(tree);
          
          // Auto-expand first 2 levels unless explicitly collapsed
          if (!collapseAll) {
            const expandedSet = new Set<number>();
            tree.forEach((node: UserHierarchyNode) => {
              expandedSet.add(node.user.id);
              node.children.forEach((childNode) => {
                expandedSet.add(childNode.user.id);
              });
            });
            setExpandedNodes(expandedSet);
          }
        } else {
          throw new Error('Tree endpoint not available');
        }
      } catch (treeError) {
        console.warn('Tree endpoint not available, building tree from users:', treeError);
        // Build hierarchy tree from users data if tree endpoint doesn't exist
        if (usersResponse.ok) {
          const users = await usersResponse.json();
          const tree = buildHierarchyTreeFromUsers(users);
          setHierarchyTree(tree);
        } else {
          setError('Failed to load hierarchy data');
        }
      }
    } catch (err) {
      setError('Error loading hierarchy data');
      console.error('Error loading hierarchy:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = async (user: User) => {
    setSelectedUser(user);
    
    // Load reporting chain for selected user
    try {
      const headers = {
        'authorization': ctx?.apiKey ?? '',
        'Content-Type': 'application/json'
      };
      const response = await fetch(`${ctx?.baseUrl}/user-hierarchy/reporting-chain/${user.id}`, { headers });
      if (response.ok) {
        const chain = await response.json();
        setReportingChain(chain);
      }
    } catch (err) {
      console.error('Error loading reporting chain:', err);
    }
  };

  const handleNodeToggle = (userId: number) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
    } else {
      newExpanded.add(userId);
    }
    setExpandedNodes(newExpanded);
  };

  const handleExpandAll = () => {
    const allNodeIds = new Set<number>();
    const collectIds = (nodes: UserHierarchyNode[]) => {
      nodes.forEach(node => {
        allNodeIds.add(node.user.id);
        if (node.children.length > 0) {
          collectIds(node.children);
        }
      });
    };
    collectIds(hierarchyTree);
    setExpandedNodes(allNodeIds);
    setCollapseAll(false);
  };

  const handleCollapseAll = () => {
    setExpandedNodes(new Set());
    setCollapseAll(true);
  };

  const handleUpdateReporting = async (userId: number, newManagerId: number | null) => {
    try {
      setLoading(true);
      const headers = {
        'authorization': ctx?.apiKey ?? '',
        'Content-Type': 'application/json'
      };
      
      const response = await fetch(`${ctx?.baseUrl}/user-hierarchy/update-reporting`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          userId,
          newManagerId,
        }),
      });

      if (response.ok) {
        const { user: updatedUser } = await response.json();
        setSaveMessage('Reporting relationship updated successfully');

        // Update the user in the allUsers list
        const updatedUsers = allUsers.map(u => u.id === updatedUser.id ? updatedUser : u);
        setAllUsers(updatedUsers);

        // Rebuild the hierarchy tree from the updated user list
        const newTree = buildHierarchyTreeFromUsers(updatedUsers);
        setHierarchyTree(newTree);

        // Update the selected user if it's the one that was changed
        if (selectedUser && selectedUser.id === updatedUser.id) {
          setSelectedUser(updatedUser);
        }
        
        // Also update the reporting chain view
        if (selectedUser && selectedUser.id === userId) {
            const chainResponse = await fetch(`${ctx?.baseUrl}/user-hierarchy/reporting-chain/${userId}`, { headers });
            if (chainResponse.ok) {
                const chain = await chainResponse.json();
                setReportingChain(chain);
            }
        }

        setTimeout(() => setSaveMessage(null), 3000);
      } else {
        const error = await response.json();
        setError(error.error || 'Failed to update reporting relationship');
      }
    } catch (err) {
      setError('Error updating reporting relationship');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getAuthorizationLevelColor = (level: number): string => {
    const colors = ['#f44336', '#ff9800', '#2196f3', '#4caf50', '#9c27b0', '#607d8b'];
    return colors[level - 1] || '#757575';
  };

  if (loading) {
    return (
      <Box className={classes.root}>
        <Box className={classes.loadingContainer}>
          <CircularProgress size={40} />
          <Typography variant="body1" style={{ marginLeft: 16 }}>
            Loading User Hierarchy...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={classes.root}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {/* Simple Header */}
      <Box className={classes.header}>
        <Typography variant="h5" className={classes.headerTitle}>
          <AccountTreeIcon style={{ verticalAlign: 'middle', marginRight: 8, fontSize: '1.5rem' }} />
          User Hierarchy
        </Typography>
      </Box>

      <Grid container spacing={2}>
        {/* Left Column - Tree and Controls */}
        <Grid item xs={12} md={8}>
          {/* Hierarchy Tree Widget with Inline Controls */}
          <Paper className={classes.widget}>
            <Box className={classes.widgetHeader}>
              <Typography className={classes.widgetTitle}>
                <AccountTreeIcon style={{ marginRight: 6, fontSize: '0.9rem' }} />
                Organization Tree
              </Typography>
              <Box className={classes.compactButtons}>
                <Button
                  size="small"
                  variant="text"
                  startIcon={<ExpandMoreIcon style={{ fontSize: '0.9rem' }} />}
                  onClick={handleExpandAll}
                  className={classes.compactButton}
                >
                  Expand
                </Button>
                <Button
                  size="small"
                  variant="text"
                  startIcon={<ExpandLessIcon style={{ fontSize: '0.9rem' }} />}
                  onClick={handleCollapseAll}
                  className={classes.compactButton}
                >
                  Collapse
                </Button>
                <Button
                  size="small"
                  variant="text"
                  startIcon={<VisibilityIcon style={{ fontSize: '0.9rem' }} />}
                  onClick={() => setShowUnassignedDialog(true)}
                  color="secondary"
                  className={classes.compactButton}
                >
                  Unassigned ({unassignedUsers.length})
                </Button>
              </Box>
            </Box>
            <Box className={classes.treeWidget}>
              <HierarchyTreeView
                hierarchyTree={hierarchyTree}
                expandedNodes={expandedNodes}
                selectedUser={selectedUser}
                authorizationLevels={authorizationLevels}
                allUsers={allUsers}
                onUserSelect={handleUserSelect}
                onNodeToggle={handleNodeToggle}
                onUpdateReporting={handleUpdateReporting}
                getAuthorizationLevelColor={getAuthorizationLevelColor}
              />
            </Box>
          </Paper>
        </Grid>

        {/* Right Column - User Details */}
        <Grid item xs={12} md={4}>
          {selectedUser && (
            <UserDetailPanel
              user={selectedUser}
              authorizationLevels={authorizationLevels}
              allUsers={allUsers}
              onUserUpdate={loadHierarchyData}
            />
          )}

          {reportingChain.length > 0 && (
            <ReportingChainView
              reportingChain={reportingChain}
              authorizationLevels={authorizationLevels}
              getAuthorizationLevelColor={getAuthorizationLevelColor}
            />
          )}
        </Grid>
      </Grid>

      {/* Unassigned Users Dialog */}
      <Dialog
        open={showUnassignedDialog}
        onClose={() => setShowUnassignedDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Unassigned Users ({unassignedUsers.length})
        </DialogTitle>
        <DialogContent className={classes.unassignedDialog}>
          {/* Sticky Search Section */}
          <Box className={classes.dialogSearchSection}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              These users are not part of any hierarchy (no manager and no subordinates):
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label="Search User"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Type to search users..."
            />
          </Box>

          {/* Scrollable List Section */}
          <Box className={classes.dialogListSection}>
            <List>
              {unassignedUsers
                .filter(user => user.name.toLowerCase().includes(searchQuery.toLowerCase()))
                .map((user) => (
                <ListItem
                  key={user.id}
                  className={classes.userItem}
                  onClick={() => {
                    handleUserSelect(user);
                    setShowUnassignedDialog(false);
                  }}
                >
                  <ListItemAvatar>
                    <Avatar>
                      <PersonIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={user.name}
                    secondary={user.department?.name || 'No department'}
                  />
                  <Chip
                    size="small"
                    label={user.authorizationLevel 
                      ? authorizationLevels.find(l => l.value === Number(user.authorizationLevel))?.name || 
                        authorizationLevels.find(l => l.name.toUpperCase() === String(user.authorizationLevel).toUpperCase())?.name ||
                        'Unknown'
                      : 'No level'
                    }
                    style={{
                      backgroundColor: user.authorizationLevel 
                        ? getAuthorizationLevelColor(
                            authorizationLevels.find(l => l.value === Number(user.authorizationLevel))?.value ||
                            authorizationLevels.find(l => l.name.toUpperCase() === String(user.authorizationLevel).toUpperCase())?.value ||
                            6
                          )
                        : '#757575',
                      color: 'white'
                    }}
                  />
                </ListItem>
              ))}
              {unassignedUsers
                .filter(user => user.name.toLowerCase().includes(searchQuery.toLowerCase()))
                .length === 0 && (
                <ListItem>
                  <ListItemText 
                    primary={searchQuery ? `No users found matching "${searchQuery}"` : "No unassigned users found"} 
                  />
                </ListItem>
              )}
            </List>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUnassignedDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>


      {/* Success/Error Messages */}
      <Snackbar
        open={!!saveMessage}
        autoHideDuration={3000}
        onClose={() => setSaveMessage(null)}
      >
        <Alert severity="success" onClose={() => setSaveMessage(null)}>
          {saveMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={5000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserHierarchyPage;
